import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RecruitingTeamSection } from "./RecruitingTeamSection";
import { TimelineSection } from "./TimelineSection";
import { HiringSection } from "./HiringSection";
import { HiringGridHeader } from "./HiringGridHeader";
import { HiringSummarySection } from "./HiringSummarySection";
import { RecruitingGoalsSection } from "./RecruitingGoalsSection";
import { useToast } from "@/components/ui/use-toast";
import { submitToHubSpot } from "@/integrations/hubspot/client";
import { getRecruitersNeeded, formatCalculationData } from "@/utils/calculatorHelpers";
import { ContactDialog } from "./ContactDialog";

interface CalculatorFormProps {
  onCalculate: (values: CalculatorInputs) => void;
}

export interface CalculatorInputs {
  hiringData: {
    tech: HiringData;
    gtm: HiringData;
    gna: HiringData;
    exec: HiringData;
  };
  timelineMonths: number;
  rampPeriodMonths: number;
  recruitingTeam: {
    currentRecruiters: {
      tech: number;
      gtm: number;
      gna: number;
      exec: number;
    };
    fteHeadcount: number;
    outsourcedHeadcount: number;
  };
  recruitingGoals: {
    tech: number;
    gtm: number;
    gna: number;
    exec: number;
  };
  contactInfo: {
    name: string;
    email: string;
    company: string;
  };
}

export interface HiringData {
  startingHeadcount: number;
  incrementalHires: number;
  endingHeadcount: number;
  backfillPercentage: number;
  backfillHires: number;
  plannedGrossHires: number;
}

export const CalculatorForm = ({ onCalculate }: CalculatorFormProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = React.useState(false);
  const [showContactDialog, setShowContactDialog] = React.useState(false);
  const [isCalculated, setIsCalculated] = React.useState(false);
  
  // Load saved contact info from localStorage
  const savedContactInfo = localStorage.getItem('talentful_contact_info');
  const initialContactInfo: { name: string; email: string; company: string; } = savedContactInfo ? JSON.parse(savedContactInfo) : {
    name: "",
    email: "",
    company: "",
  };

  const [values, setValues] = React.useState<CalculatorInputs>({
    hiringData: {
      tech: {
        startingHeadcount: 0,
        incrementalHires: 0,
        endingHeadcount: 0,
        backfillPercentage: 0,
        backfillHires: 0,
        plannedGrossHires: 0,
      },
      gtm: {
        startingHeadcount: 0,
        incrementalHires: 0,
        endingHeadcount: 0,
        backfillPercentage: 0,
        backfillHires: 0,
        plannedGrossHires: 0,
      },
      gna: {
        startingHeadcount: 0,
        incrementalHires: 0,
        endingHeadcount: 0,
        backfillPercentage: 0,
        backfillHires: 0,
        plannedGrossHires: 0,
      },
      exec: {
        startingHeadcount: 0,
        incrementalHires: 0,
        endingHeadcount: 0,
        backfillPercentage: 0,
        backfillHires: 0,
        plannedGrossHires: 0,
      },
    },
    timelineMonths: 12,
    rampPeriodMonths: 1,
    recruitingTeam: {
      currentRecruiters: {
        tech: 0,
        gtm: 0,
        gna: 0,
        exec: 0,
      },
      fteHeadcount: 0,
      outsourcedHeadcount: 0,
    },
    recruitingGoals: {
      tech: 0,
      gtm: 0,
      gna: 0,
      exec: 0,
    },
    contactInfo: initialContactInfo,
  });

  const [calculatedValues, setCalculatedValues] = React.useState<CalculatorInputs | null>(null);

  React.useEffect(() => {
    if (!calculatedValues) {
      const updatedHiringData = Object.entries(values.hiringData).reduce(
        (acc, [key, data]) => {
          const startingHeadcount = Number(data.startingHeadcount) || 0;
          const incrementalHires = Number(data.incrementalHires) || 0;
          const endingHeadcount = startingHeadcount + incrementalHires;
          const backfillHires = Math.round(
            (startingHeadcount * data.backfillPercentage) / 100
          );
          const plannedGrossHires = incrementalHires + backfillHires;

          return {
            ...acc,
            [key]: {
              ...data,
              endingHeadcount,
              backfillHires,
              plannedGrossHires,
            },
          };
        },
        {} as typeof values.hiringData
      );

      setValues((prev) => ({
        ...prev,
        hiringData: updatedHiringData,
      }));
    }
  }, [values.hiringData, calculatedValues]);

  const handleHiringDataChange = (category: keyof typeof values.hiringData) => (
    field: string,
    value: number
  ) => {
    setValues((prev) => ({
      ...prev,
      hiringData: {
        ...prev.hiringData,
        [category]: {
          ...prev.hiringData[category],
          [field]: value,
        },
      },
    }));
  };

  const handleContactSubmit = async (contactInfo: {
    name: string;
    email: string;
    company: string;
  }) => {
    setLoading(true);
    try {
      setCalculatedValues(values);
      
      const calculationData = JSON.parse(JSON.stringify({
        ...values,
        contactInfo,
      }));

      console.log('\n=== Calculator Form Submission ===');
      console.log('Raw calculation data:', calculationData);
      console.log('Hiring data:', calculationData.hiringData);
      console.log('Timeline:', {
        months: calculationData.timelineMonths,
        rampPeriod: calculationData.rampPeriodMonths
      });
      console.log('Recruiting team:', calculationData.recruitingTeam);
      console.log('Recruiting goals:', calculationData.recruitingGoals);

      // Submit to HubSpot
      await submitToHubSpot(calculationData);
      console.log('Calculation data submitted successfully to HubSpot');
      
      onCalculate(calculationData);
      setShowContactDialog(false);
      setIsCalculated(true);
      
      setTimeout(() => {
        const resultsElement = document.querySelector('.results-section');
        if (resultsElement) {
          resultsElement.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);

      toast({
        title: "Success!",
        description: "Your calculation has been saved and results are ready.",
      });
    } catch (error: any) {
      console.error("Error submitting to HubSpot:", error);
      toast({
        title: "Error",
        description: "Failed to save your calculation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateClick = (e: React.FormEvent) => {
    e.preventDefault();
    
    const totalPercentage = values.recruitingTeam.fteHeadcount + values.recruitingTeam.outsourcedHeadcount;
    if (totalPercentage !== 100) {
      toast({
        title: "Invalid Team Split",
        description: "FTE and outsourced headcount percentages must sum to 100%",
        variant: "destructive",
      });
      return;
    }

    // Check if any department has hires but no recruiting goal
    const departmentsWithErrors = Object.entries(values.hiringData).filter(([dept, data]) => {
      const hasHires = data.plannedGrossHires > 0;
      const hasGoal = values.recruitingGoals[dept as keyof typeof values.recruitingGoals] > 0;
      return hasHires && !hasGoal;
    });

    if (departmentsWithErrors.length > 0) {
      const departments = departmentsWithErrors.map(([dept]) => {
        switch(dept) {
          case 'tech': return 'Tech & Product';
          case 'gtm': return 'Go-to-market';
          case 'gna': return 'G&A';
          case 'exec': return 'Executive';
          default: return dept;
        }
      });
      
      toast({
        title: "Missing Recruiting Goals",
        description: `Please set hiring goals (hires/month) for: ${departments.join(', ')}`,
        variant: "destructive",
      });
      return;
    }
    
    setShowContactDialog(true);
  };

  const handleRecruitingTeamChange = (field: string) => (value: number) => {
    setValues((prev) => {
      if (field.startsWith('currentRecruiters.')) {
        const recruiterType = field.split('.')[1];
        return {
          ...prev,
          recruitingTeam: {
            ...prev.recruitingTeam,
            currentRecruiters: {
              ...prev.recruitingTeam.currentRecruiters,
              [recruiterType]: value,
            },
          },
        };
      }
      return {
        ...prev,
        recruitingTeam: {
          ...prev.recruitingTeam,
          [field]: value,
        },
      };
    });
  };

  return (
    <Card className="p-6">
      <form onSubmit={handleCalculateClick} className="space-y-6">
        <div className="space-y-4 border p-4 rounded-lg">
          <h3 className="font-semibold text-lg mb-4">Hiring Plan</h3>
          <div className="overflow-x-auto -mx-4 px-4 md:overflow-visible md:px-0 md:mx-0">
            <div className="min-w-[800px] md:min-w-0">
              <HiringGridHeader />
              <HiringSection
                category="Tech & Product"
                values={values.hiringData.tech}
                onChange={handleHiringDataChange("tech")}
              />
              <HiringSection
                category="Go-to-market"
                values={values.hiringData.gtm}
                onChange={handleHiringDataChange("gtm")}
              />
              <HiringSection
                category="G&A"
                values={values.hiringData.gna}
                onChange={handleHiringDataChange("gna")}
              />
              <HiringSection
                category="Executive"
                values={values.hiringData.exec}
                onChange={handleHiringDataChange("exec")}
              />
              <HiringSummarySection hiringData={values.hiringData} />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="border p-4 rounded-lg">
            <RecruitingTeamSection
              currentRecruiters={values.recruitingTeam.currentRecruiters}
              fteHeadcount={values.recruitingTeam.fteHeadcount}
              outsourcedHeadcount={values.recruitingTeam.outsourcedHeadcount}
              onChange={handleRecruitingTeamChange}
              isCalculated={false}
            />
          </div>

          <div className="border p-4 rounded-lg">
            <RecruitingGoalsSection
              goals={values.recruitingGoals}
              onChange={(field) => (value) => {
                setValues((prev) => ({
                  ...prev,
                  recruitingGoals: {
                    ...prev.recruitingGoals,
                    [field]: value,
                  },
                }));
              }}
              isCalculated={false}
            />
          </div>
        </div>

        <TimelineSection
          timelineMonths={values.timelineMonths}
          rampPeriodMonths={values.rampPeriodMonths}
          onChange={(field) => (e) =>
            setValues((prev) => ({
              ...prev,
              [field]: e.target.value === "" ? 0 : Number(e.target.value),
            }))
          }
        />

        <Button
          type="submit"
          className="w-full bg-[#FF6F61] hover:bg-[#FF5B4D] text-white px-8 py-6 text-lg rounded-full"
          disabled={loading}
        >
          Calculate Hiring Needs
        </Button>
      </form>

      <ContactDialog
        open={showContactDialog}
        onOpenChange={setShowContactDialog}
        onSubmit={handleContactSubmit}
        loading={loading}
      />
    </Card>
  );
};